import React, { useEffect, useRef, useState } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Alert, FormText } from 'reactstrap'
import { useMutation } from '@apollo/client'
import { CREATE_MESSAGE_TEMPLATE, UPDATE_MESSAGE_TEMPLATE } from './Mutations'
import { TEMPLATE_VARIABLES } from '../templateVariables'
import './CreateEditMessageTemplateModal.css'

function SimpleRichEditor({ value, onChange, disabled }) {
    const editorRef = useRef(null)
    const [editorMode, setEditorMode] = useState('design')

    useEffect(() => {
        const editor = editorRef.current
        if (!editor || editorMode !== 'design') return
        const nextValue = value || ''
        const isFocused = document.activeElement === editor
        if (!isFocused && editor.innerHTML !== nextValue) {
            editor.innerHTML = nextValue
        }
    }, [value, editorMode])

    const applyCommand = (command, commandValue = null) => {
        if (disabled) return
        if (editorMode !== 'design') return
        const editor = editorRef.current
        if (!editor) return
        editor.focus()
        if (command === 'createLink') {
            const link = window.prompt('Enter URL (include https://):')
            if (!link) return
            document.execCommand(command, false, link)
        } else {
            document.execCommand(command, false, commandValue)
        }
        onChange(editor.innerHTML)
    }

    return (
        <div className="template-rich-editor">
            <div className="template-rich-editor__bar">
                <div className="template-rich-editor__tools">
                    <button type="button" className="template-rich-editor__tool" disabled={disabled || editorMode !== 'design'} onClick={() => applyCommand('bold')}><strong>B</strong></button>
                    <button type="button" className="template-rich-editor__tool" disabled={disabled || editorMode !== 'design'} onClick={() => applyCommand('italic')}><em>I</em></button>
                    <button type="button" className="template-rich-editor__tool" disabled={disabled || editorMode !== 'design'} onClick={() => applyCommand('underline')}><u>U</u></button>
                    <button type="button" className="template-rich-editor__tool" disabled={disabled || editorMode !== 'design'} onClick={() => applyCommand('insertUnorderedList')}>Bullets</button>
                    <button type="button" className="template-rich-editor__tool" disabled={disabled || editorMode !== 'design'} onClick={() => applyCommand('insertOrderedList')}>Numbered</button>
                    <button type="button" className="template-rich-editor__tool" disabled={disabled || editorMode !== 'design'} onClick={() => applyCommand('createLink')}>Link</button>
                    <button type="button" className="template-rich-editor__tool" disabled={disabled || editorMode !== 'design'} onClick={() => applyCommand('removeFormat')}>Clear</button>
                </div>
                <div className="template-rich-editor__modes">
                    <button
                        type="button"
                        className={`template-rich-editor__mode ${editorMode === 'design' ? 'is-active' : ''}`}
                        onClick={() => setEditorMode('design')}
                        disabled={disabled}
                    >
                        Design
                    </button>
                    <button
                        type="button"
                        className={`template-rich-editor__mode ${editorMode === 'html' ? 'is-active' : ''}`}
                        onClick={() => setEditorMode('html')}
                        disabled={disabled}
                    >
                        HTML
                    </button>
                </div>
            </div>
            {editorMode === 'design' ? (
                <div
                    ref={editorRef}
                    className="template-rich-editor__surface"
                    contentEditable={!disabled}
                    suppressContentEditableWarning
                    data-placeholder="Write your email template..."
                    onInput={(e) => onChange(e.currentTarget.innerHTML)}
                />
            ) : (
                <textarea
                    className="template-rich-editor__source"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={disabled}
                    spellCheck={false}
                />
            )}
            <div className="template-rich-editor__hint">
                {editorMode === 'design'
                    ? 'Use Design mode for quick formatting.'
                    : 'Use HTML mode for precise template markup.'}
            </div>
        </div>
    )
}

export default function CreateEditMessageTemplateModal({ isOpen, toggle, template, onSuccess }) {
    const isEdit = Boolean(template)
    const [title, setTitle] = useState('')
    const [shortcut, setShortcut] = useState('')
    const [content, setContent] = useState('')
    const [contentHtml, setContentHtml] = useState('')
    const [isActive, setIsActive] = useState(true)
    const [errors, setErrors] = useState([])
    const [varsOpen, setVarsOpen] = useState(false)
    const [copiedKey, setCopiedKey] = useState(null)

    const slugifyShortcut = (value = '') => {
        return value
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '')
    }

    const handleTitleChange = (e) => {
        const nextTitle = e.target.value
        const autoFromCurrentTitle = slugifyShortcut(title)
        const nextAutoShortcut = slugifyShortcut(nextTitle)
        const shouldAutofill = !shortcut || shortcut === autoFromCurrentTitle

        setTitle(nextTitle)
        if (shouldAutofill) {
            setShortcut(nextAutoShortcut)
        }
    }

    const htmlToPlainText = (htmlValue = '') => {
        if (!htmlValue) return ''
        const parser = new DOMParser()
        const doc = parser.parseFromString(htmlValue, 'text/html')
        const blockTags = new Set(['DIV', 'P', 'LI', 'TR', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'TABLE'])
        let text = ''

        const walk = (node) => {
            if (!node) return
            if (node.nodeType === Node.TEXT_NODE) {
                text += node.nodeValue || ''
                return
            }
            if (node.nodeType !== Node.ELEMENT_NODE) return

            const tagName = node.tagName
            if (tagName === 'BR') {
                text += '\n'
                return
            }

            const isBlock = blockTags.has(tagName)
            if (isBlock && text && !text.endsWith('\n')) {
                text += '\n'
            }

            Array.from(node.childNodes).forEach(walk)

            if (isBlock && !text.endsWith('\n')) {
                text += '\n'
            }
        }

        Array.from(doc.body?.childNodes || []).forEach(walk)

        text = text
            .replace(/\u00a0/g, ' ')
            .replace(/[ \t]+\n/g, '\n')
            .replace(/\n[ \t]+/g, '\n')
            .replace(/\n{3,}/g, '\n\n')
            .trim()
        return text
    }

    const handleHtmlContentChange = (nextHtml) => {
        setContentHtml(nextHtml)
        setContent(htmlToPlainText(nextHtml))
    }

    useEffect(() => {
        if (isOpen) {
            setTitle(template?.title || '')
            setShortcut((template?.shortcut || '').replace(/^\/+/, ''))
            setContent(template?.content || '')
            setContentHtml(template?.contentHtml || '')
            setIsActive(template?.isActive ?? true)
            setErrors([])
        }
    }, [isOpen, template])

    const [createTemplate, { loading: creating }] = useMutation(CREATE_MESSAGE_TEMPLATE, {
        onCompleted: (data) => {
            const response = data?.messagingCreateMessageTemplate
            if (response?.ok) {
                if (onSuccess) {
                    onSuccess('Template created successfully.')
                }
                toggle()
            } else {
                setErrors(response?.errors || ['Unable to create template.'])
            }
        },
        onError: (error) => {
            setErrors([error.message])
        }
    })

    const [updateTemplate, { loading: updating }] = useMutation(UPDATE_MESSAGE_TEMPLATE, {
        onCompleted: (data) => {
            const response = data?.messagingUpdateMessageTemplate
            if (response?.ok) {
                if (onSuccess) {
                    onSuccess('Template updated successfully.')
                }
                toggle()
            } else {
                setErrors(response?.errors || ['Unable to update template.'])
            }
        },
        onError: (error) => {
            setErrors([error.message])
        }
    })

    const handleSubmit = (e) => {
        e.preventDefault()
        setErrors([])

        const trimmedTitle = title.trim()
        const trimmedContent = content.trim()
        const trimmedContentHtml = contentHtml.trim()
        const normalizedShortcut = shortcut.trim().replace(/^\/+/, '')

        if (!trimmedTitle) {
            setErrors(['Title is required.'])
            return
        }
        if (!trimmedContent && !trimmedContentHtml) {
            setErrors(['Provide plain content or HTML content.'])
            return
        }

        const variables = {
            title: trimmedTitle,
            content: trimmedContent || null,
            contentHtml: trimmedContentHtml || null,
            shortcut: normalizedShortcut || null,
            isActive,
        }

        if (isEdit) {
            updateTemplate({
                variables: {
                    ...variables,
                    id: template.id,
                }
            })
        } else {
            createTemplate({
                variables
            })
        }
    }

    const loading = creating || updating

    const hasPlain = Boolean(content.trim())
    const hasHtml = Boolean(contentHtml.trim())

    return (
        <Modal isOpen={isOpen} size="lg" className="template-modal">
            <ModalHeader>
                <div className="template-modal__header">
                    <div className="template-modal__title">{isEdit ? 'Edit Template' : 'Create Template'}</div>
                    <div className="template-modal__subtitle">Build reusable replies for agents across channels.</div>
                </div>
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
                <ModalBody>
                    {errors.length > 0 && (
                        <Alert color="danger">
                            {errors.map((error, index) => (
                                <div key={index}>{error}</div>
                            ))}
                        </Alert>
                    )}
                    <div className="template-modal__meta">
                        <span className="template-modal__meta-label">Template Modes</span>
                        <span className={`template-modal__pill ${hasPlain ? 'is-active' : ''}`}>Plain</span>
                        <span className={`template-modal__pill ${hasHtml ? 'is-active' : ''}`}>HTML</span>
                    </div>

                    <FormGroup>
                        <Label for="templateTitle">Title *</Label>
                        <Input
                            id="templateTitle"
                            type="text"
                            value={title}
                            onChange={handleTitleChange}
                            placeholder="e.g. Greeting"
                            disabled={loading}
                            required
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label for="templateShortcut">Shortcut</Label>
                        <div className="template-modal__shortcut-wrap">
                            <span className="template-modal__shortcut-prefix">/</span>
                            <Input
                            id="templateShortcut"
                            type="text"
                            value={shortcut}
                            onChange={(e) => setShortcut(e.target.value.replace(/^\/+/, ''))}
                            placeholder="greeting"
                            disabled={loading}
                        />
                        </div>
                        <FormText color="muted">
                            Agents can type this shortcut in the composer to quickly insert the template.
                        </FormText>
                    </FormGroup>
                    <FormGroup>
                        <div style={{ marginBottom: '12px' }}>
                            <button
                                type="button"
                                onClick={() => setVarsOpen(v => !v)}
                                style={{
                                    background: 'none',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    padding: '4px 10px',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    color: '#374151',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                }}
                            >
                                <i className="fa fa-code" aria-hidden="true" />
                                Variables
                                <i className={`fa fa-chevron-${varsOpen ? 'up' : 'down'}`} aria-hidden="true" style={{ fontSize: '10px' }} />
                            </button>
                        </div>
                        {varsOpen && (() => {
                            const categories = [...new Set(TEMPLATE_VARIABLES.map(v => v.category))]
                            return (
                                <div style={{
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    marginBottom: '12px',
                                    background: '#f9fafb',
                                }}>
                                    <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '10px', margin: '0 0 10px 0' }}>
                                        Click a variable to copy its syntax. Paste it into your template where you want it substituted.
                                    </p>
                                    {categories.map(category => (
                                        <div key={category} style={{ marginBottom: '10px' }}>
                                            <span style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                {category}
                                            </span>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                                                {TEMPLATE_VARIABLES.filter(v => v.category === category).map(v => (
                                                    <button
                                                        key={v.key}
                                                        type="button"
                                                        title={`${v.label} — e.g. "${v.example}"`}
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(`{{${v.key}}}`)
                                                            setCopiedKey(v.key)
                                                            setTimeout(() => setCopiedKey(null), 1500)
                                                        }}
                                                        style={{
                                                            background: copiedKey === v.key ? '#dcfce7' : '#fff',
                                                            border: `1px solid ${copiedKey === v.key ? '#86efac' : '#d1d5db'}`,
                                                            borderRadius: '4px',
                                                            padding: '2px 8px',
                                                            fontSize: '12px',
                                                            fontFamily: 'monospace',
                                                            cursor: 'pointer',
                                                            color: copiedKey === v.key ? '#166534' : '#1f2937',
                                                            transition: 'all 0.15s ease',
                                                        }}
                                                    >
                                                        {copiedKey === v.key ? '✓ Copied' : `{{${v.key}}}`}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )
                        })()}
                    </FormGroup>

                    <FormGroup>
                        <Label for="templateContentHtml">HTML Content</Label>
                        <SimpleRichEditor
                            value={contentHtml}
                            onChange={handleHtmlContentChange}
                            disabled={loading}
                        />
                        <FormText color="muted">
                            Used in email composer/send when present. You can still paste raw HTML into the editor.
                            Use <code>{'{{variable}}'}</code> syntax — variables are resolved when the template is applied in a conversation.
                        </FormText>
                    </FormGroup>
                    <FormGroup>
                        <Label for="templateContent">Plain Content</Label>
                        <Input
                            id="templateContent"
                            type="textarea"
                            rows="6"
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder="Write the canned response..."
                            disabled={loading}
                        />
                        <FormText color="muted">
                            Used for SMS/WhatsApp and as the email fallback body.
                        </FormText>
                    </FormGroup>
                    <FormGroup check>
                        <Label check>
                            <Input
                                type="checkbox"
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                                disabled={loading}
                            />{' '}
                            Mark template as active
                        </Label>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle} disabled={loading}>
                        Cancel
                    </Button>
                    <Button color="primary" type="submit" disabled={loading}>
                        {loading ? 'Saving…' : isEdit ? 'Update Template' : 'Create Template'}
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    )
}
